import { BMCESnapshotI } from "shared/lib/interfaces/account";
import { TIME_INTERVALS } from "shared/lib/constants/time";

/**
 * How long the AutoSyncStep waits for the lookalike/ICS channel to sync the
 * meeting before it stops claiming to detect and points the SDR at manual
 * entry, and how often it polls account-details while waiting.
 */
export const LOOKALIKE_SYNC_WINDOW_MS = TIME_INTERVALS.FIVE_MINUTES;
export const LOOKALIKE_SYNC_POLL_MS = 10 * TIME_INTERVALS.ONE_SECOND;

/**
 * OAuth calendar detection is the second-tier fallback for lookalike bookings:
 * it reads the AE's connected calendar in case the ICS invite never lands (the
 * SDR didn't add the lookalike guest, or the provider can't carry one). It
 * starts a few seconds after AutoSyncStep opens so the ICS channel gets first
 * shot, then polls until it finds the event or the shared sync window elapses.
 */
export const OAUTH_DETECT_START_DELAY_MS = 10 * TIME_INTERVALS.ONE_SECOND;
export const OAUTH_DETECT_POLL_MS = 15 * TIME_INTERVALS.ONE_SECOND;

export type LookalikeSyncStatus = "detecting" | "found" | "timeout";

/**
 * Combined outcome of the two lookalike detection channels (ICS + OAuth
 * calendar). `oauth_found` is transient — the modal routes it to the
 * confirm-then-book step rather than rendering it in AutoSyncStep.
 */
export type LookalikeDetectionOutcome =
  | "detecting"
  | "ics_found"
  | "oauth_found"
  | "timeout";

/**
 * True when the account's booked-meeting snapshot represents a meeting the
 * lookalike/ICS channel actually synced for THIS booking:
 *  - it carries an `ics_uid` (email-rcvr processed the invite),
 *  - it is not cancelled, and
 *  - its `ics_uid` differs from the baseline captured before the booking, so a
 *    pre-existing meeting (e.g. the old event on a reschedule) is not mistaken
 *    for the freshly-synced one. A null/empty baseline means there was no prior
 *    ICS meeting, so any live `ics_uid` counts.
 */
export const isMeetingIcsSynced = (
  bmce: BMCESnapshotI | null | undefined,
  baselineIcsUid: string | null
): boolean => {
  const icsUid = bmce?.ics_uid;

  if (!icsUid) {
    return false;
  }

  if (bmce?.canceled_at) {
    return false;
  }

  if (baselineIcsUid && icsUid === baselineIcsUid) {
    return false;
  }

  return true;
};

/**
 * Pure state resolver for AutoSyncStep — kept free of React/timers so the
 * detection rules can be unit-tested directly. `found` wins over `timeout`
 * (if the meeting synced right at the deadline, report success).
 */
export const resolveLookalikeSyncStatus = ({
  bmce,
  baselineIcsUid,
  elapsedMs,
  windowMs = LOOKALIKE_SYNC_WINDOW_MS,
}: {
  bmce: BMCESnapshotI | null | undefined;
  baselineIcsUid: string | null;
  elapsedMs: number;
  windowMs?: number;
}): LookalikeSyncStatus => {
  if (isMeetingIcsSynced(bmce, baselineIcsUid)) {
    return "found";
  }

  if (elapsedMs >= windowMs) {
    return "timeout";
  }

  return "detecting";
};

/**
 * Arbitrates the two detection channels into a single outcome. ICS is the
 * primary channel for lookalike campaigns, so a synced ICS meeting wins over an
 * OAuth calendar hit. OAuth still wins over a bare ICS timeout: if the event is
 * on the AE's calendar we can book it even though the invite never synced. Kept
 * pure (no React/timers) so the priority is unit-testable.
 */
export const resolveLookalikeDetectionOutcome = ({
  icsStatus,
  oauthFound,
}: {
  icsStatus: LookalikeSyncStatus;
  oauthFound: boolean;
}): LookalikeDetectionOutcome => {
  if (icsStatus === "found") {
    return "ics_found";
  }

  if (oauthFound) {
    return "oauth_found";
  }

  if (icsStatus === "timeout") {
    return "timeout";
  }

  return "detecting";
};
